import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, XIcon, CheckCircleIcon, UserIcon, SearchIcon, FilterIcon, HashIcon, EditIcon, HomeIcon, ClockIcon, ExternalLinkIcon, CalendarIcon } from 'lucide-react';
import { CallStatusModal } from '../../components/rental/CallStatusModal';
import { QualifiedLeadDetailsPanel } from '../../components/rental/QualifiedLeadDetailsPanel';
import { LeadDetailsPanel } from '../../components/rental/LeadDetailsPanel';
import { ViewingScheduleModal } from '../../components/rental/ViewingScheduleModal';
import { useRental } from '../../contexts/RentalContext';
import { toast } from 'sonner';
interface ActionTimestamp {
  action: string;
  timestamp: string;
  agent: string;
}
export interface BuyerLead {
  leadId: string;
  createdAt: string;
  entryDate: string;
  entryTime: string;
  name: string;
  phone: string;
  propertyId?: string;
  callCenterMemo: string;
  callCompleted: boolean;
  callStatus: string;
  fullName?: string;
  propertyType?: string;
  rooms?: number;
  budgetMin?: number;
  budgetMax?: number;
  zones: string[];
  excludedFloors?: string;
  maxFloors?: number;
  bathrooms?: number;
  constructionYear?: number;
  qualificationMemo?: string;
  qualified: boolean;
  actionHistory: ActionTimestamp[];
}
interface RentalLeadsListProps {
  allLeads: BuyerLead[];
  onLeadUpdate: (leadId: string, updates: Partial<BuyerLead>) => void;
  onAddActionToHistory: (leadId: string, action: string, agent?: string) => void;
  searchQuery: string;
  filters: any;
}
const callStatusOptions = [{
  value: 'nou',
  label: 'Nou',
  needsQualification: false
}, {
  value: 'de-apelat',
  label: 'De apelat',
  needsQualification: false
}, {
  value: 'nu-raspunde',
  label: 'Nu răspunde',
  needsQualification: false
}, {
  value: 'calificare',
  label: 'Calificare ✨',
  needsQualification: true
}, {
  value: 'a-inchiriat',
  label: 'A închiriat deja ⛔',
  needsQualification: false
}, {
  value: 'numar-invalid',
  label: 'Număr invalid ⛔',
  needsQualification: false
}, {
  value: 'nu-colaboreaza',
  label: 'Nu colaborează ⛔',
  needsQualification: false
}, {
  value: 'comision-redus',
  label: 'Oferă comision redus ⛔',
  needsQualification: false
}, {
  value: 'vrea-vizionare',
  label: 'Vrea vizionare ✓',
  needsQualification: true
}, {
  value: 'nu-vrea-vizionare',
  label: 'Nu vrea vizionare - Calificare ✓',
  needsQualification: true
}, {
  value: 'doreste-vanzare',
  label: 'Dorește vânzare ⛔',
  needsQualification: false
}];
const isInvalidStatus = (status: string) => {
  return ['a-inchiriat', 'numar-invalid'].includes(status);
};
const needsQualification = (status: string) => {
  const option = callStatusOptions.find(opt => opt.value === status);
  return option?.needsQualification || false;
};
export function RentalLeadsList({
  allLeads,
  onLeadUpdate,
  onAddActionToHistory,
  searchQuery,
  filters
}: RentalLeadsListProps) {
  const {
    addViewing
  } = useRental();
  const [selectedLead, setSelectedLead] = useState<BuyerLead | null>(null);
  const [filter, setFilter] = useState<'all' | 'new' | 'toCall' | 'called' | 'qualified'>('all');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [leadForStatusUpdate, setLeadForStatusUpdate] = useState<BuyerLead | null>(null);
  const [isViewingModalOpen, setIsViewingModalOpen] = useState(false);
  const [leadForViewing, setLeadForViewing] = useState<BuyerLead | null>(null);
  // Filter leads
  const filteredLeads = useMemo(() => {
    return allLeads.filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!lead.leadId.toLowerCase().includes(query) && !lead.name.toLowerCase().includes(query) && !lead.phone.includes(query)) {
          return false;
        }
      }
      if (filters.leadId && !lead.leadId.toLowerCase().includes(filters.leadId.toLowerCase())) return false;
      if (filters.phone && !lead.phone.includes(filters.phone)) return false;
      if (filters.dateFrom && lead.entryDate < filters.dateFrom) return false;
      if (filters.dateTo && lead.entryDate > filters.dateTo) return false;
      if (filters.status && lead.callStatus !== filters.status) return false;
      if (filters.budgetMin && lead.budgetMin && lead.budgetMin < parseInt(filters.budgetMin)) return false;
      if (filters.budgetMax && lead.budgetMax && lead.budgetMax > parseInt(filters.budgetMax)) return false;
      // Apply column filter
      switch (filter) {
        case 'new':
          return lead.callStatus === 'nou';
        case 'toCall':
          return lead.callStatus === 'de-apelat' || lead.callStatus === 'nu-raspunde';
        case 'called':
          return lead.callCompleted && !lead.qualified && !needsQualification(lead.callStatus);
        case 'qualified':
          return lead.qualified || needsQualification(lead.callStatus);
        default:
          return true;
      }
    });
  }, [allLeads, searchQuery, filters, filter]);
  // Calculate stats
  const stats = useMemo(() => {
    return {
      all: allLeads.length,
      new: allLeads.filter(l => l.callStatus === 'nou').length,
      toCall: allLeads.filter(l => l.callStatus === 'de-apelat' || l.callStatus === 'nu-raspunde').length,
      called: allLeads.filter(l => l.callCompleted && !l.qualified && !needsQualification(l.callStatus)).length,
      qualified: allLeads.filter(l => l.qualified || needsQualification(l.callStatus)).length
    };
  }, [allLeads]);
  const getStepBadge = (lead: BuyerLead) => {
    if (lead.qualified) {
      return <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-id="element-11141">
          Calificat
        </span>;
    }
    if (needsQualification(lead.callStatus)) {
      return <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold" data-id="element-11142">
          De Calificat
        </span>;
    }
    if (lead.callCompleted) {
      return <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold" data-id="element-11143">
          Apelat
        </span>;
    }
    if (lead.callStatus === 'de-apelat') {
      return <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold" data-id="element-11144">
          De Apelat
        </span>;
    }
    return <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold" data-id="element-11145">
        Nou
      </span>;
  };
  const getStatusBadge = (lead: BuyerLead) => {
    const statusConfig: Record<string, {
      label: string;
      color: string;
    }> = {
      nou: {
        label: 'Nou',
        color: 'bg-blue-100 text-blue-700'
      },
      'de-apelat': {
        label: 'De apelat',
        color: 'bg-purple-100 text-purple-700'
      },
      'nu-raspunde': {
        label: 'Nu răspunde',
        color: 'bg-orange-100 text-orange-700'
      },
      calificare: {
        label: '✨ Calificare',
        color: 'bg-emerald-100 text-emerald-700'
      },
      'vrea-vizionare': {
        label: 'Vrea vizionare',
        color: 'bg-green-100 text-green-700'
      },
      'nu-vrea-vizionare': {
        label: 'Nu vrea vizionare - Calificare',
        color: 'bg-slate-100 text-slate-700'
      },
      'nu-colaboreaza': {
        label: '🔄 Nu colaborează',
        color: 'bg-orange-100 text-orange-700'
      },
      'comision-redus': {
        label: '💰 Comision redus',
        color: 'bg-yellow-100 text-yellow-700'
      },
      'doreste-vanzare': {
        label: '🏠 Dorește vânzare',
        color: 'bg-blue-100 text-blue-700'
      },
      'a-inchiriat': {
        label: '⛔ A închiriat deja',
        color: 'bg-red-100 text-red-700'
      },
      'numar-invalid': {
        label: '⛔ Număr invalid',
        color: 'bg-red-100 text-red-700'
      }
    };
    const config = statusConfig[lead.callStatus] || {
      label: lead.callStatus,
      color: 'bg-slate-100 text-slate-600'
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.color}`} data-id="element-11146">
        {config.label}
      </span>;
  };
  const handleQuickAction = (lead: BuyerLead) => {
    if (lead.callStatus === 'nou') {
      // Preluare lead
      onLeadUpdate(lead.leadId, {
        callStatus: 'de-apelat'
      });
      onAddActionToHistory(lead.leadId, 'Lead preluat de agent');
    } else if (lead.callStatus === 'de-apelat' || lead.callStatus === 'nu-raspunde' || lead.callStatus === 'comision-redus' || lead.callStatus === 'nu-colaboreaza') {
      // Open status modal for call result (reapelare pentru toate aceste statusuri)
      setLeadForStatusUpdate(lead);
      setIsStatusModalOpen(true);
    } else {
      // Open details - use enhanced panel for qualified leads
      setSelectedLead(lead);
    }
  };
  const handleScheduleViewing = (lead: BuyerLead) => {
    setLeadForViewing(lead);
    setIsViewingModalOpen(true);
  };
  const handleViewingSubmit = (viewingData: any) => {
    const newViewing = {
      id: `V${Date.now()}`,
      leadId: viewingData.leadId,
      clientName: viewingData.clientName,
      clientPhone: viewingData.clientPhone,
      propertyId: viewingData.propertyId,
      ownerName: viewingData.ownerName,
      ownerPhone: viewingData.ownerPhone,
      date: viewingData.date,
      time: viewingData.time,
      duration: 30,
      status: 'scheduled' as const,
      meetingPoint: 'La proprietate',
      commissionPaidBy: viewingData.commissionPaidBy,
      ownerCommission: viewingData.ownerCommission,
      clientCommission: viewingData.clientCommission,
      agentNotes: viewingData.notes,
      createdAt: new Date().toISOString()
    };
    addViewing(newViewing);
    onAddActionToHistory(viewingData.leadId, `Vizionare programată pentru ${viewingData.date} la ${viewingData.time}`);
    toast.success('✅ Vizionare programată cu succes!');
    setIsViewingModalOpen(false);
    setLeadForViewing(null);
  };
  const handleStatusSubmit = (status: string, qualificationData?: Partial<BuyerLead>) => {
    if (!leadForStatusUpdate) return;
    // SPECIAL CASE: "Nu răspunde" - RĂMÂNE în "De Apelat"
    if (status === 'nu-raspunde') {
      onLeadUpdate(leadForStatusUpdate.leadId, {
        callStatus: status,
        callCompleted: false // IMPORTANT: false pentru a rămâne în "De Apelat"
      });
      onAddActionToHistory(leadForStatusUpdate.leadId, 'Apel finalizat - Status: Nu răspunde (poate fi reapelat)');
      // Update selectedLead if it's the same lead
      if (selectedLead?.leadId === leadForStatusUpdate.leadId) {
        setSelectedLead({
          ...selectedLead,
          callStatus: status,
          callCompleted: false
        });
      }
      setIsStatusModalOpen(false);
      setLeadForStatusUpdate(null);
      return;
    }
    // TOATE CELELALTE STATUSURI - Trec în "Apelate" (callCompleted: true)
    const updates: Partial<BuyerLead> = {
      callCompleted: true,
      callStatus: status
    };
    // If qualification data is provided, include it and mark as qualified
    if (qualificationData) {
      Object.assign(updates, qualificationData, {
        qualified: true
      });
    }
    onLeadUpdate(leadForStatusUpdate.leadId, updates);
    const statusLabel = callStatusOptions.find(o => o.value === status)?.label || status;
    onAddActionToHistory(leadForStatusUpdate.leadId, `Apel finalizat - Status: ${statusLabel}`);
    if (qualificationData) {
      onAddActionToHistory(leadForStatusUpdate.leadId, 'Lead calificat');
    }
    // Update selectedLead if it's the same lead
    if (selectedLead?.leadId === leadForStatusUpdate.leadId) {
      setSelectedLead({
        ...selectedLead,
        ...updates
      });
    }
    setIsStatusModalOpen(false);
    setLeadForStatusUpdate(null);
  };
  const getActionButtonText = (lead: BuyerLead) => {
    if (lead.callStatus === 'nou') return 'Preluare';
    if (lead.callStatus === 'de-apelat') return 'Apel Finalizat';
    if (lead.callStatus === 'nu-raspunde' || lead.callStatus === 'comision-redus' || lead.callStatus === 'nu-colaboreaza') return '📞 Reapelare';
    return 'Detalii';
  };
  const getActionButtonColor = (lead: BuyerLead) => {
    if (lead.callStatus === 'nou') return 'bg-blue-500 hover:bg-blue-600';
    if (lead.callStatus === 'de-apelat') return 'bg-purple-500 hover:bg-purple-600';
    if (lead.callStatus === 'nu-raspunde' || lead.callStatus === 'comision-redus' || lead.callStatus === 'nu-colaboreaza') return 'bg-amber-500 hover:bg-amber-600';
    return 'bg-[#357BEB] hover:bg-[#2a62bc]';
  };
  return <div className="space-y-4" data-id="element-11147">
      {/* Filter Tabs */}
      <div className="flex bg-slate-200/50 p-1 rounded-xl overflow-x-auto" data-id="element-11148">
        <FilterTab label="Toate" active={filter === 'all'} onClick={() => setFilter('all')} count={stats.all} data-id="element-11149" />
        <FilterTab label="Lead Nou" active={filter === 'new'} onClick={() => setFilter('new')} count={stats.new} data-id="element-11150" />
        <FilterTab label="De Apelat" active={filter === 'toCall'} onClick={() => setFilter('toCall')} count={stats.toCall} data-id="element-11151" />
        <FilterTab label="Apelate" active={filter === 'called'} onClick={() => setFilter('called')} count={stats.called} data-id="element-11152" />
        <FilterTab label="Calificare" active={filter === 'qualified'} onClick={() => setFilter('qualified')} count={stats.qualified} data-id="element-11153" />
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-11154">
        <table className="w-full text-sm text-left" data-id="element-11155">
          <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-11156">
            <tr data-id="element-11157">
              <th className="px-4 py-4 text-xs" data-id="element-11158">ID Lead</th>
              <th className="px-4 py-4 text-xs" data-id="element-11159">Nume</th>
              <th className="px-4 py-4 text-xs" data-id="element-11160">Telefon</th>
              <th className="px-4 py-4 text-xs" data-id="element-11161">Proprietate</th>
              <th className="px-4 py-4 text-xs" data-id="element-11162">Buget</th>
              <th className="px-4 py-4 text-xs" data-id="element-11163">Step</th>
              <th className="px-4 py-4 text-xs" data-id="element-11164">Status</th>
              <th className="px-4 py-4 text-xs" data-id="element-11165">Data</th>
              <th className="px-4 py-4 text-xs text-right" data-id="element-11166">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-11167">
            {filteredLeads.map(lead => <motion.tr key={lead.leadId} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className={`hover:bg-slate-50 transition-colors ${isInvalidStatus(lead.callStatus) ? 'bg-red-50/30' : lead.qualified ? 'bg-emerald-50/20' : ''}`} data-id="element-11168">
                <td className="px-4 py-3 font-mono text-slate-500 text-xs font-bold" data-id="element-11169">
                  {lead.leadId}
                </td>
                <td className="px-4 py-3 font-bold text-[#132C53]" data-id="element-11170">
                  {lead.name}
                </td>
                <td className="px-4 py-3" data-id="element-11171">
                  <a href={`tel:${lead.phone}`} className="flex items-center text-xs text-slate-600 hover:text-[#357BEB] font-mono" data-id="element-11172">
                    <PhoneIcon className="w-3 h-3 mr-2" data-id="element-11173" />
                    {lead.phone}
                  </a>
                </td>
                <td className="px-4 py-3" data-id="element-11174">
                  {lead.propertyId ? <span className="flex items-center text-xs text-[#357BEB] font-bold" data-id="element-11175">
                      <HomeIcon className="w-3 h-3 mr-1" data-id="element-11176" />
                      {lead.propertyId}
                    </span> : <span className="text-slate-300 text-xs" data-id="element-11177">—</span>}
                </td>
                <td className="px-4 py-3" data-id="element-11178">
                  {lead.budgetMin && lead.budgetMax ? <span className="text-[#357BEB] font-bold text-xs whitespace-nowrap" data-id="element-11179">
                      {lead.budgetMin}-{lead.budgetMax} €
                    </span> : <span className="text-slate-300 text-xs" data-id="element-11180">—</span>}
                </td>
                <td className="px-4 py-3" data-id="element-11181">{getStepBadge(lead)}</td>
                <td className="px-4 py-3" data-id="element-11182">{getStatusBadge(lead)}</td>
                <td className="px-4 py-3 text-xs text-slate-500" data-id="element-11183">
                  {lead.entryDate} {lead.entryTime}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-11184">
                  <div className="flex items-center justify-end gap-2" data-id="element-11185">
                    {lead.qualified ? <>
                        <button onClick={() => handleScheduleViewing(lead)} className="px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm whitespace-nowrap text-white bg-blue-500 hover:bg-blue-600 flex items-center gap-1" data-id="element-11186">
                          <CalendarIcon className="w-3 h-3" data-id="element-11187" />
                          Programează
                        </button>
                        <button onClick={() => setSelectedLead(lead)} className="px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200" data-id="element-11188">
                          👁️ Detalii
                        </button>
                      </> : <>
                        <button onClick={() => handleQuickAction(lead)} disabled={isInvalidStatus(lead.callStatus)} className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm whitespace-nowrap text-white ${isInvalidStatus(lead.callStatus) ? 'bg-slate-300 cursor-not-allowed' : getActionButtonColor(lead)}`} data-id="element-11189">
                          {getActionButtonText(lead)}
                        </button>
                        <button onClick={() => setSelectedLead(lead)} className="px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200" data-id="element-11190">
                          👁️ Detalii
                        </button>
                      </>}
                  </div>
                </td>
              </motion.tr>)}
          </tbody>
        </table>

        {filteredLeads.length === 0 && <div className="text-center py-12 text-slate-400" data-id="element-11191">
            <UserIcon className="w-16 h-16 mx-auto mb-4 opacity-50" data-id="element-11192" />
            <p className="text-lg font-medium" data-id="element-11193">Niciun lead găsit</p>
            <p className="text-sm" data-id="element-11194">
              Încearcă să modifici filtrele sau căutarea
            </p>
          </div>}
      </div>

      {/* Call Status Modal */}
      <CallStatusModal isOpen={isStatusModalOpen} onClose={() => {
      setIsStatusModalOpen(false);
      setLeadForStatusUpdate(null);
    }} onSubmit={handleStatusSubmit} leadName={leadForStatusUpdate?.name || ''} currentLead={leadForStatusUpdate || undefined} data-id="element-11195" />

      {/* Viewing Schedule Modal */}
      {leadForViewing && <ViewingScheduleModal isOpen={isViewingModalOpen} onClose={() => {
      setIsViewingModalOpen(false);
      setLeadForViewing(null);
    }} onSubmit={handleViewingSubmit} leadData={{
      leadId: leadForViewing.leadId,
      clientName: leadForViewing.fullName || leadForViewing.name,
      clientPhone: leadForViewing.phone,
      propertyId: leadForViewing.propertyId || ''
    }} data-id="element-11196" />}

      {/* Side Panel - Conditional rendering based on qualification status */}
      <AnimatePresence data-id="element-11197">
        {selectedLead && <>
            {selectedLead.qualified ? <QualifiedLeadDetailsPanel lead={selectedLead} onClose={() => setSelectedLead(null)} data-id="element-11198" /> : <LeadDetailsPanel lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdateStatus={() => {
          setLeadForStatusUpdate(selectedLead);
          setIsStatusModalOpen(true);
        }} onLeadUpdate={(leadId, updates) => {
          onLeadUpdate(leadId, updates);
          // Update selectedLead to reflect changes
          setSelectedLead({
            ...selectedLead,
            ...updates
          });
        }} data-id="element-11199" />}
          </>}
      </AnimatePresence>
    </div>;
}
function FilterTab({
  label,
  active,
  onClick,
  count
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
}) {
  return <button onClick={onClick} className={`
        px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2
        ${active ? 'bg-white text-[#132C53] shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'}
      `} data-id="element-11200">
      {label}
      {count !== undefined && <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-slate-100' : 'bg-slate-200'}`} data-id="element-11201">
          {count}
        </span>}
    </button>;
}